import { useCallback, useEffect, useState } from 'react'

import { type ApplicationFormData } from '@/models/applicationSchema'
import {
  getApplicationStatus,
  submitApplication,
  type ValidationErrorResponse,
} from '@/services/api'

type AppState =
  | 'no-id'
  | 'loading'
  | 'ready'
  | 'not-found'
  | 'already-submitted'
  | 'server-error'
  | 'submitting'
  | 'success'
  | 'status-changed'

interface UseApplicationReturn {
  state: AppState
  submitError: string | null
  errorId: string | null
  serverErrors: Record<string, string> | null
  checkStatus: () => void
  submit: (data: ApplicationFormData) => Promise<void>
  dismissError: () => void
}

export function useApplication(recordId: string | null): UseApplicationReturn {
  const [state, setState] = useState<AppState>(recordId ? 'loading' : 'no-id')
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [errorId, setErrorId] = useState<string | null>(null)
  const [serverErrors, setServerErrors] = useState<Record<string, string> | null>(null)

  const checkStatus = useCallback(() => {
    if (!recordId) {
      setState('no-id')
      return
    }

    setState('loading')

    getApplicationStatus(recordId)
      .then(() => {
        setState('ready')
      })
      .catch((err: unknown) => {
        const status = getAxiosStatus(err)
        if (status === 404) {
          setState('not-found')
          return
        }
        if (status === 409) {
          setState('already-submitted')
          return
        }
        setErrorId(getErrorId(err))
        setState('server-error')
      })
  }, [recordId])

  useEffect(() => {
    checkStatus()
  }, [checkStatus])

  const submit = useCallback(
    async (data: ApplicationFormData) => {
      if (!recordId) return

      setState('submitting')
      setSubmitError(null)
      setErrorId(null)
      setServerErrors(null)

      try {
        await submitApplication(recordId, data)
        setState('success')
      } catch (err: unknown) {
        const status = getAxiosStatus(err)
        if (status === 400) {
          const responseData = getAxiosData(err) as ValidationErrorResponse | undefined
          if (responseData?.errors) {
            setServerErrors(responseData.errors)
          }
          setState('ready')
          return
        }
        if (status === 409) {
          setState('status-changed')
          return
        }
        setErrorId(getErrorId(err))
        setSubmitError('Submission failed. Please try again.')
        setState('ready')
      }
    },
    [recordId]
  )

  const dismissError = useCallback(() => {
    setSubmitError(null)
    setErrorId(null)
  }, [])

  return { state, submitError, errorId, serverErrors, checkStatus, submit, dismissError }
}

function getAxiosStatus(err: unknown): number | undefined {
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const response = (err as { response?: { status?: number } }).response
    return response?.status
  }
  return undefined
}

function getAxiosData(err: unknown): unknown {
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const response = (err as { response?: { data?: unknown } }).response
    return response?.data
  }
  return undefined
}

function getErrorId(err: unknown): string | null {
  const data = getAxiosData(err)
  if (typeof data === 'object' && data !== null && 'error_id' in data) {
    const id = (data as { error_id?: unknown }).error_id
    if (typeof id === 'string' && id !== '') return id
  }
  return null
}
